import { Settings } from '@xeetr/types/models'
import { AlertCircle, Loader2, Save, X } from 'lucide-react'
import { FieldErrors, SubmitHandler, useFormContext, useFormState } from 'react-hook-form'

import { Button } from '@/components/ui/button'
import { showToast } from '@/utils/toastMessage'

interface Props {
  onSubmit: SubmitHandler<Settings>
  loading: boolean
}

const SettingsFooter = ({ onSubmit, loading }: Props) => {
  const { reset, handleSubmit, control } = useFormContext<Settings>()
  const { isDirty } = useFormState({ control })

  const handleInvalidState = (e: FieldErrors<Settings>) => {
    console.error(e)
    showToast({ detail: e.root?.message, severity: 'error' })
  }

  const isDisabled = !isDirty || loading

  return (
    <div className="flex h-20 w-full items-center justify-between gap-2 px-4 sm:gap-4 sm:px-6">
      {/* Status Indicator - Hidden on mobile, visible on tablet+ */}
      <div className="hidden items-center gap-2 text-sm md:flex">
        {isDirty && !loading && (
          <>
            <AlertCircle className="h-4 w-4 text-amber-500" />
            <span className="text-muted-foreground">Nicht gespeicherte Änderungen</span>
          </>
        )}
        {loading && (
          <>
            <Loader2 className="text-primary h-4 w-4 animate-spin" />
            <span className="text-muted-foreground">Wird gespeichert...</span>
          </>
        )}
        {!isDirty && !loading && (
          <span className="text-muted-foreground">Alle Änderungen gespeichert</span>
        )}
      </div>

      {/* Mobile status indicator - Icon only */}
      <div className="flex items-center gap-2 md:hidden">
        {isDirty && !loading && <AlertCircle className="h-4 w-4 text-amber-500" />}
        {loading && <Loader2 className="text-primary h-4 w-4 animate-spin" />}
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-2 sm:gap-3">
        <Button
          variant="ghost"
          size="default"
          onClick={() => reset()}
          disabled={isDisabled}
          className="sm:min-w-[120px]"
        >
          <X className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Verwerfen</span>
        </Button>
        <Button
          size="default"
          onClick={handleSubmit(onSubmit, handleInvalidState)}
          disabled={isDisabled}
          className="sm:min-w-[120px]"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin sm:mr-2" />
              <span className="hidden sm:inline">Speichern...</span>
            </>
          ) : (
            <>
              <Save className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Speichern</span>
            </>
          )}
        </Button>
      </div>
    </div>
  )
}

export default SettingsFooter
